import * as React from "react";
import * as ReactDOM from "react-dom";
import * as utils from "../../utils/utils";
import { Overlay } from "../overlay/overlay";

interface IProperties
{
    children?: JSX.Element | JSX.Element[],
    title?: string,
    titleIcon?: string,
    titleIconColor?: string,
    titleIndent?: number,
    width?: number,
    height?: number,
    minWidth?: number,
    minHeight?: number,
    position?: {
        top: number,
        left: number,
    };
    isOpen?: boolean,
    isMaximized?: boolean,
    isResizable?: boolean,
    canContentExtendToTitle?: boolean,
    onTitleClick?: () => void,
    onClosing?: () => boolean,
    onClosed?: () => void,
    onEscapeKeyUp?: () => void,
    onEnterKeyUp?: () => void,
    onResize?: () => void,
    onWindowState?: (isMaximized?: boolean) => void
}

interface IState
{
    width?: number;
    height?: number;
    minWidth?: number;
    minHeight?: number;
    position?: {
        top?: number;
        left?: number;
    };
    isOpen?: boolean;
    isMaximized?: boolean;
}

enum MouseMoveMode
{
    move,
    resizeW,
    resizeE,
    resizeS,
    resizeSE
}

export class Window extends React.Component<IProperties, IState>
{
    // Enthält die Position der <Window /> Komponente die beim erstmaligem Klick
    // auf den DragHandle gesetzt wird.
    private windowStartingPosition: {
        width: number;
        height: number;
        top: number;
        left: number;
        mouseX: number;
        mouseY: number;
    };

    // Gibt an ob die <Window /> Komponente verschoben oder in der Größe verändert
    // werden soll.
    private mouseMoveMode: MouseMoveMode;

    // Enthält die Methode die die Mausbewegungen erfasst.
    private mouseMoveFunc: any;

    // Standard Eingeschaften.
    public static defaultProps: Partial<IProperties> = {
        titleIndent: 10,
        isOpen: false,
        isMaximized: false,
        isResizable: true,
        canContentExtendToTitle: false
    };

    /**
     * Initialisert die Komponente.
     * @param props
     */
    constructor(props: IProperties)
    {
        super(props);

        var width: number = (props.width != null) ? props.width : window.innerWidth - (70 * 2);
        var height: number = (props.height != null) ? props.height : window.innerHeight - (70 * 2);
        var minWidth: number = (props.minWidth != null) ? props.minWidth : width / 2;
        var minHeight: number = (props.minHeight != null) ? props.minHeight : height / 2;

        this.state = {
            width: width,
            height: height,
            minWidth: minWidth,
            minHeight: minHeight,
            position: {
                top: (props.position != null && props.position.top != null) ? props.position.top : ((window.innerHeight / 2) - (height / 2)),
                left: (props.position != null && props.position.left != null) ? props.position.left : ((window.innerWidth / 2) - (width / 2)),
            },
            isOpen: this.props.isOpen,
            isMaximized: this.props.isMaximized
        };

        window.addEventListener('mouseup', this.onMouseUp.bind(this), false);
    }

    componentWillReceiveProps(nextProps: IProperties)
    {
        var size = this.setWindowSize(nextProps);
        this.setState({
            width: size.width,
            height: size.height,
            minWidth: size.minWidth,
            minHeight: size.minHeight
        });
    }

    private setWindowSize(props: IProperties)
    {
        var width: number = (props.width != null) ? props.width : window.innerWidth - (70 * 2);
        var height: number = (props.height != null) ? props.height : window.innerHeight - (70 * 2);
        var minWidth: number = (props.minWidth != null) ? props.minWidth : width / 2;
        var minHeight: number = (props.minHeight != null) ? props.minHeight : height / 2;

        return {
            width,
            height,
            minWidth,
            minHeight
        };
    }

    /**
     * Ändert die Position des Fensters.
     * @param mouseX Momentane Mauszeiger Position.
     * @param mouseY Momentane Mauszeiger Position.
     */
    private moveWindow(mouseX: number, mouseY: number): void
    {
        const component: HTMLElement = this.refs.window as HTMLElement;
        var top: number = mouseY - this.windowStartingPosition.top;
        var left: number = mouseX - this.windowStartingPosition.left;
        var right: number = window.innerWidth - left - component.offsetWidth;
        var bottom: number = window.innerHeight - top - component.offsetHeight;

        if (top < 0)
            top = 0

        if (left < 0)
            left = 0;

        if (right < 0)
            left = left - (right * -1);

        if (bottom < 0)
            top = top - (bottom * -1);

        this.setState({
            position: {
                top: top,
                left: left
            }
        });
    }

    /**
     * Verändert die Größe des Fensters anhand der Mausbewegungen.
     * @param mouseX Momentane Mauszeiger Position.
     * @param mouseY Momentane Mauszeiger Position.
     * @param direction Die Richtung in die das Fenster vergrößert/verkleinert werden soll.
     */
    private resizeWindow(mouseX: number, mouseY: number, direction: MouseMoveMode)
    {
        const component: HTMLElement = this.refs.window as HTMLElement;
        var state: IState;

        switch (direction)
        {
            case MouseMoveMode.resizeW:
                {
                    let left: number = mouseX - this.windowStartingPosition.left;
                    let width: number = this.windowStartingPosition.width + this.windowStartingPosition.mouseX - left;

                    if (width > this.state.minWidth)
                    {
                        state = {
                            width: width,
                            position: {
                                top: component.offsetTop,
                                left: (left < 0) ? 0 : left
                            }
                        };
                    }
                }
                break;
            case MouseMoveMode.resizeE:
                {
                    let width: number = (mouseX >= window.innerWidth) ? window.innerWidth - component.offsetLeft : mouseX - component.offsetLeft;
                    if (width > this.state.minWidth)
                    {
                        state = {
                            width: width
                        };
                    }
                }
                break;
            case MouseMoveMode.resizeS:
                {
                    let height: number = (mouseY >= window.innerHeight) ? window.innerHeight - component.offsetTop : mouseY - component.offsetTop;

                    if (height > this.state.minHeight)
                    {
                        state = {
                            height: height
                        };
                    }
                }
                break;
            case MouseMoveMode.resizeSE:
                {
                    let width: number = (mouseX >= window.innerWidth) ? window.innerWidth - component.offsetLeft : mouseX - component.offsetLeft;
                    let height: number = (mouseY >= window.innerHeight) ? window.innerHeight - component.offsetTop : mouseY - component.offsetTop;

                    if (width > this.state.minWidth && height > this.state.minHeight)
                    {
                        state = {
                            width: width,
                            height: height
                        };
                    }
                    else if (width > this.state.minWidth)
                    {
                        state = {
                            width: width
                        };
                    }
                    else if (height > this.state.minHeight)
                    {
                        state = {
                            height: height
                        };
                    }
                }
                break;
        }

        this.props.onResize && this.props.onResize();
        
        if (state == null)
            return;

        this.setState(state);
    }

    /**
     * Wird bei einem Klick auf den DragHandle ausgelöst.
     * @param e Das auslösende Event.
     */
    private onWindowHandleMouseDown(e: MouseEvent, mode: MouseMoveMode): void
    {
        if (this.state.isMaximized)
            return;

        // Momentane Position der Komponente ermitteln.
        var { x, y } = utils.getMousePosition(e);
        const component = this.refs.window as HTMLElement;
        this.windowStartingPosition = {
            width: component.offsetWidth,
            height: component.offsetHeight,
            top: y - component.offsetTop,
            left: x - component.offsetLeft,
            mouseX: x,
            mouseY: y
        };

        // Die MouseMove Methode global speichern, damit diese später auch
        // wieder vom event listener entfernt werden kann,
        this.mouseMoveFunc = this.onMouseMove.bind(this);

        // Auf die Mausbewegung im Browserfenster reagieren.
        this.mouseMoveMode = mode;
        window.addEventListener("mousemove", this.mouseMoveFunc, true);
    }

    /**
     * Wird ausgeführt sobald im Browserfenster die Maus bewegt wird.
     * @param e Das auslösende Event.
     */
    private onMouseMove(e: MouseEvent)
    {
        if (this.state.isMaximized)
            return;

        var { x, y } = utils.getMousePosition(e);
        switch (this.mouseMoveMode)
        {
            case MouseMoveMode.move:
                this.moveWindow(x, y);
                break;
            case MouseMoveMode.resizeW:
            case MouseMoveMode.resizeE:
            case MouseMoveMode.resizeS:
            case MouseMoveMode.resizeSE:
                this.resizeWindow(x, y, this.mouseMoveMode);
                break;
        }
    }

    /**
     * Wird ausgeführt wenn im Browserfenster der Mausklick gelöst wird.
     * @param e Das auslösende Event.
     */
    private onMouseUp(e: MouseEvent): void
    {
        window.removeEventListener("mousemove", this.mouseMoveFunc, true);
    }

    /**
     * Maximiert oder verkleinert das Fenster.
     */
    private onMaximizeButtonClick()
    {
        if (this.state.isMaximized)
        {
            this.setState({
                isMaximized: false
            }, () => this.props.onWindowState && this.props.onWindowState(false));
        }
        else
        {
            this.setState({
                isMaximized: true
            }, () => this.props.onWindowState && this.props.onWindowState(true));
        }
    }

    /**
     * Schließt das Fenster.
     */
    private onCloseButtonClick()
    {
        const { onClosing, onClosed } = this.props;
        var close: boolean = onClosing != null ? onClosing() : true;

        if (close)
        {
            this.setState({
                isOpen: false
            });

            onClosed && onClosed();
        }
        
    }

    /**
     * Wird ausgeführt, sobald im Fenster eine Taste betätigt wird.
     * @param e Das auslösende Event.
     */
    private onKeyUp = (e: React.KeyboardEvent<HTMLElement>) =>
    {
        const { onEscapeKeyUp, onEnterKeyUp } = this.props;

        if (e.key === "Escape")
        {
            onEscapeKeyUp && onEscapeKeyUp();
        }
        else if (e.key === "Enter")
        {
            onEnterKeyUp && onEnterKeyUp();
        }
    }

    /**
     * Generiert den Titel der <Window /> Komponente.
     */
    private getTitleComponent(indentation: number = 10)
    {
        const { title, titleIcon, titleIconColor, titleIndent, onTitleClick } = this.props;

        var icon: JSX.Element = null;
        if (titleIcon != null)
        {
            icon = (
                <i
                    className={`window_title_icon mdi ${titleIcon === "loading" ? "mdi-spin" : ""} mdi-${titleIcon}`}
                    style={{
                        color: titleIconColor
                    }}></i>
            );
        }

        var titleText: JSX.Element = null;
        if (title != null)
        {
            titleText = (
                <a
                    className={`window_title_text ${onTitleClick != null ? "window_title_text--is-link" : ""}`}
                    onClick={onTitleClick}>{title}</a>
            );
        }

        if (titleIcon || titleText)
        {
            return (
                <div
                    className="window_title"
                    style={{
                        left: indentation
                    }}>
                    {icon}
                    {titleText}
                </div>
            );
        }
        
        return null;
    }

    /**
     * Generiert die Elemente die für das bewegen oder vergrößern/verkleinern des Fensters zuständig sind.
     */
    private getWindowHandlesComponent()
    {
        if (this.props.isResizable && !this.state.isMaximized)
        {
            const handles = [];
            handles.push(
                <div
                    className="window_drag-handle"
                    onMouseDown={(e) => this.onWindowHandleMouseDown(e.nativeEvent, MouseMoveMode.move)}></div>
            );

            handles.push(
                <div
                    className="window_resize-handle window_resize-handle--w"
                    onMouseDown={(e) => this.onWindowHandleMouseDown(e.nativeEvent, MouseMoveMode.resizeW)}></div>
            );

            handles.push(
                <div
                    className="window_resize-handle window_resize-handle--e"
                    onMouseDown={(e) => this.onWindowHandleMouseDown(e.nativeEvent, MouseMoveMode.resizeE)}></div>
            );

            handles.push(
                <div
                    className="window_resize-handle window_resize-handle--s"
                    onMouseDown={(e) => this.onWindowHandleMouseDown(e.nativeEvent, MouseMoveMode.resizeS)}></div>
            );

            handles.push(
                <div
                    className="window_resize-thumb"
                    onMouseDown={(e) => this.onWindowHandleMouseDown(e.nativeEvent, MouseMoveMode.resizeSE)}>
                    <i className="mdi mdi-resize-bottom-right"></i>
                </div>
            );

            return handles;
        }

        return null;
    }

    /**
     * Rendert die Komponente.
     */
    render()
    {
        if (!this.state.isOpen)
            return null;

        // Inline CSS für die momentane Position des Fensters erstellen.
        var windowPos = {
            width: this.state.width,
            height: this.state.height,
            minWidth: this.state.minWidth,
            minHeight: this.state.minHeight,
            top: this.state.position.top,
            left: this.state.position.left
        }

        // Fenster maximieren wenn nötig.
        if (this.state.isMaximized)
        {
            windowPos.top = 0;
            windowPos.left = 0;
            windowPos.width = window.innerWidth;
            windowPos.height = window.innerHeight;
        }

        return (
            <Overlay>
                <div
                    ref="window"
                    className={`window ${this.state.isMaximized ? "window--is-maximized" : ""}`}
                    tabIndex={-1}
                    onKeyUp={this.onKeyUp}
                    style={windowPos}>
                    {this.getWindowHandlesComponent()}
                    {this.getTitleComponent(this.props.titleIndent)}
                    <div className="window_actions">
                        {
                            this.props.isResizable &&
                            <div
                                className="window_actions_btn"
                                onClick={this.onMaximizeButtonClick.bind(this)}>
                                <i className={`mdi mdi-${this.state.isMaximized ? "window-restore" : "window-maximize"}`}></i>
                            </div>
                        }
                        
                        <div
                            className="window_actions_btn window_actions_btn--is-close"
                            onClick={this.onCloseButtonClick.bind(this)}>
                            <i className="mdi mdi-window-close"></i>
                        </div>
                    </div>
                    <div
                        className="window_content"
                        style={{
                            top: this.props.canContentExtendToTitle ? 0 : 28
                        }}>
                        {this.props.children}
                    </div>
                </div>
            </Overlay>
        );
    }
}